/**
 * AdminOnboardingAuditPage — dedicated audit trail for admin actions
 * against employee onboarding: manual overrides + re-acknowledgement
 * requests. Filters (action / date range / search) + Excel export.
 *
 * Backed by:
 *   GET  /api/admin/onboarding-audit?action=&date_from=&date_to=&q=
 *   GET  /api/admin/onboarding-audit/export.xlsx?...
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { Download, RefreshCw, Search, ShieldCheck, RotateCcw } from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTs = (iso) => {
  if (!iso) return '—';
  try { return format(parseISO(iso), 'dd MMM yyyy, HH:mm'); }
  catch { return iso; }
};

const actionMeta = {
  admin_complete_onboarding: {
    label: 'Admin Override — Onboarding Complete',
    Icon: ShieldCheck,
    className: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/40',
  },
  request_reack: {
    label: 'Re-Acknowledgement Requested',
    Icon: RotateCcw,
    className: 'bg-amber-500/15 text-amber-500 border-amber-500/40',
  },
};

export default function AdminOnboardingAuditPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [q, setQ] = useState('');
  const [exporting, setExporting] = useState(false);

  const buildParams = useCallback(() => {
    const params = {};
    if (action !== 'all') params.action = action;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (q.trim()) params.q = q.trim();
    return params;
  }, [action, dateFrom, dateTo, q]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/onboarding-audit`, { params: buildParams() });
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load audit trail');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/onboarding-audit/export.xlsx`, {
        params: buildParams(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `onboarding_audit_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Exported to Excel');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 max-w-[1400px] mx-auto space-y-4">
        <div className="border-b border-border pb-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold uppercase tracking-wider" data-testid="page-title">
              Onboarding Audit Trail
            </h1>
            <p className="text-xs text-muted-foreground mt-1">
              Every admin action against employee onboarding — overrides, re-acknowledgement requests, and the exact reason recorded at the time.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={fetchData}
              disabled={loading}
              className="rounded-sm border-border uppercase tracking-wider text-xs h-8"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={handleExport}
              disabled={exporting || loading}
              className="rounded-sm bg-primary hover:bg-primary/90 uppercase tracking-wider text-xs h-8"
              data-testid="export-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              {exporting ? 'Exporting…' : 'Export Excel'}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-3 grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="space-y-1 sm:col-span-1">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Search</Label>
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
                <Input
                  placeholder="Employee, admin, reason…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-8 rounded-sm border-border h-9"
                  data-testid="search-input"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Action</Label>
              <Select value={action} onValueChange={setAction}>
                <SelectTrigger className="rounded-sm border-border h-9" data-testid="action-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All actions</SelectItem>
                  <SelectItem value="admin_complete_onboarding">Admin Override</SelectItem>
                  <SelectItem value="request_reack">Re-Acknowledgement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-sm border-border h-9"
                data-testid="date-from"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-sm border-border h-9"
                data-testid="date-to"
              />
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audit-table">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">When</th>
                    <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Action</th>
                    <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Employee</th>
                    <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Administrator</th>
                    <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Reason / Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={5} className="text-center py-8 text-sm text-muted-foreground italic">Loading…</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-8 text-sm text-muted-foreground italic" data-testid="empty-state">No audit records match the current filters.</td></tr>
                  ) : rows.map((r) => {
                    const meta = actionMeta[r.action] || { label: r.action, Icon: RefreshCw, className: 'bg-muted text-foreground border-border' };
                    const Icon = meta.Icon;
                    const isReack = r.action === 'request_reack';
                    return (
                      <tr
                        key={r.id}
                        className="border-b border-border/50 hover:bg-accent/30 transition-colors align-top"
                        data-testid={`audit-row-${r.id}`}
                      >
                        <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap font-mono">{fmtTs(r.created_at)}</td>
                        <td className="px-4 py-3">
                          <Badge className={`${meta.className} text-[10px] uppercase tracking-wider`}>
                            <Icon className="w-3 h-3 mr-1" />
                            {meta.label}
                          </Badge>
                          {isReack && r.extra ? (
                            <p className="text-[11px] text-muted-foreground mt-1">
                              {r.extra.kind}{r.extra.label ? ` · ${r.extra.label}` : ''}
                              {typeof r.extra.cleared_records === 'number' ? ` · cleared ${r.extra.cleared_records}` : ''}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-foreground font-medium">{r.employee_name || '—'}</p>
                          <p className="text-[11px] text-muted-foreground">
                            {r.employee_email}
                            {r.employee_code ? ` · #${r.employee_code}` : ''}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-foreground">{r.admin_name || '—'}</p>
                          <p className="text-[11px] text-muted-foreground">{r.admin_email}</p>
                        </td>
                        <td className="px-4 py-3 text-foreground/80 text-xs">
                          {r.detail || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {rows.length > 0 && (
              <div className="px-4 py-2 border-t border-border text-[11px] text-muted-foreground">
                Showing <span className="text-foreground font-medium">{rows.length}</span> record{rows.length === 1 ? '' : 's'}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
